import lodash from 'lodash'

import { padToX } from '../../lib/number.no-deps'

import { findElement } from './element.no-deps'
import type * as Flows from './types.no-deps'
import type * as Reports from '../reports/types.no-deps'
import { answerIsEmpty, omitExtraAnswerFields } from './answer.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { type LoopPath, toLoopPath } from './LoopPath.no-deps'
import { walkElements } from './generators.no-deps'
import { isLoopElement } from './typeGuards.no-deps'
import type { LoopElement } from './types.no-deps'
import type { ParentIsUserSelectable } from './UnitFilter.no-deps'

export function exhaustiveAnswerKey(elementId: Flows.ElementId, loopPath: LoopPath) {
    return `${elementId}${loopPath}`
}

export function exhaustiveAnswersCreate(flow: Flows.Flow, flowSummary: Flows.FlowSummary, actionTypeId?: number) {
    const exhaustiveAnswers: Reports.AppAnswers = {}

    for (const flowAnswer of flowSummary.answers) {
        if (answerIsEmpty(flowAnswer) && !flowAnswer.dynamicElement) continue

        const element = findElement(flow, flowAnswer.element)
        const loopPath = flowAnswer.loopPath ?? toLoopPath([])
        const tags = flowAnswer.tags ?? []
        const key = exhaustiveAnswerKey(flowAnswer.element, loopPath)

        if (element !== null) {
            exhaustiveAnswers[key] = {
                label: getElementLabel(element),
                value: flowAnswerToExhaustiveAnswerValue(flowAnswer, element),
                timestamp: flowAnswer.timestamp.toISOString(),
                loopPath,
                tags,
            }

            if (actionTypeId) {
                exhaustiveAnswers[key].activityIds = {
                    activity: actionTypeId,
                }
            }
        } else if (flowAnswer.dynamicElement) {
            const unitTypeIds: number[] = []

            for (const element of walkElements({ traverseLoops: false })(flow)) {
                if (isLoopElement(element)) {
                    const loopElement: LoopElement = element
                    const filters: ParentIsUserSelectable[] = loopElement.unitFilters.filter(
                        (filter) => filter.type === 'ParentIsUserSelectable'
                    ) as ParentIsUserSelectable[]

                    const filter = filters.find((filter) => filter.id === flowAnswer.element)

                    if (filter) {
                        unitTypeIds.push(filter.unitType)
                    }
                }
            }

            const value = dynamicElementFlowAnswerToExhaustiveAnswerValue(flowAnswer)

            if (value) {
                // the label is blank because we will need to work it out at render time in the UI
                exhaustiveAnswers[key] = {
                    label: '',
                    value: dynamicElementFlowAnswerToExhaustiveAnswerValue(flowAnswer),
                    timestamp: flowAnswer.timestamp.toISOString(),
                    loopPath,
                    tags,
                    unitTypeIds,
                    dynamicElement: flowAnswer.dynamicElement ?? false,
                }
            }
        }
    }

    for (const { elementId, result, loopPath } of flowSummary.calculations) {
        const { value, timestamp } = result

        if (value == null) continue

        const element = findElement(flow, elementId)
        const key = exhaustiveAnswerKey(elementId, loopPath)

        exhaustiveAnswers[key] = {
            label: element.label,
            value: { type: 'calculation', value },
            timestamp: timestamp && timestamp.toISOString(),
            loopPath,
        }

        if (actionTypeId) {
            exhaustiveAnswers[key].activityIds = {
                activity: actionTypeId,
            }
        }
    }

    return exhaustiveAnswers
}

function getElementLabel(element: Flows.Element | Flows.NestedElement) {
    if (element.type === 'number' && element.unitOfMeasurement != null) {
        return `${element.label} (${element.unitOfMeasurement})`
    }

    return element.label
}

function dynamicElementFlowAnswerToExhaustiveAnswerValue(flowAnswer: Flows.Answer): Reports.AnswerValue | null {
    // Remove extra fields that we won't want to upload to server
    flowAnswer = omitExtraAnswerFields(flowAnswer)

    switch (flowAnswer.type) {
        case 'confirmation':
            return { type: 'boolean', value: flowAnswer.value, rawAnswer: flowAnswer }
        case 'unit-select':
            return { type: 'units', value: flowAnswer.value, rawAnswer: flowAnswer }
    }

    return null
}

function flowAnswerToExhaustiveAnswerValue(
    flowAnswer: Flows.Answer,
    flowElement: Flows.Element | Flows.NestedElement
): Reports.AnswerValue {
    // Remove extra fields that we won't want to upload to server
    flowAnswer = omitExtraAnswerFields(flowAnswer)

    switch (flowAnswer.type) {
        case 'yes-no':
            return { type: 'boolean', value: flowAnswer.value === 'yes', rawAnswer: flowAnswer }
        case 'number':
            return { type: 'number', value: flowAnswer.value, rawAnswer: flowAnswer }
        case 'radio':
            // For choices types
            if (flowElement.type !== 'radio') throw new Error('Element type is not radio')
            return {
                type: 'string',
                value: lodash.find(flowElement.choices, { id: flowAnswer.value }).label,
                rawAnswer: flowAnswer,
            }
        case 'checkbox':
            // For choices types
            if (flowElement.type !== 'checkbox') throw new Error('Element type is not checkbox')
            return {
                type: 'select',
                value: flowAnswer.value.map((id) => lodash.find(flowElement.choices, { id }).label),
                rawAnswer: flowAnswer,
            }
        case 'image-select':
            // For choices types
            if (flowElement.type !== 'image-select') throw new Error('Element type is not image-select')
            return {
                type: 'select',
                value: flowAnswer.value.map((id) => lodash.find(flowElement.choices, { id }).label),
                rawAnswer: flowAnswer,
            }
        case 'photo':
            return { type: 'photos', value: flowAnswer.value as any[] as string[], rawAnswer: flowAnswer }
        case 'text-capture':
            return { type: 'string', value: flowAnswer.value, rawAnswer: flowAnswer }
        case 'section':
            return { type: 'section', value: flowAnswer.value, rawAnswer: flowAnswer }
        case 'confirmation':
            return { type: 'boolean', value: flowAnswer.value, rawAnswer: flowAnswer }
        case 'datetime':
            return getExhaustiveAnwserFromDatetimeAnswer(flowAnswer)
        case 'signature':
            return { type: 'photos', value: flowAnswer.value === null ? [] : [flowAnswer.value], rawAnswer: flowAnswer }
        case 'dropdown':
            // For choices types
            if (flowElement.type !== 'dropdown') throw new Error('Element type is not checkbox')
            return {
                type: 'select',
                value: flowAnswer.value.map((id) => {
                    const choice = lodash.find(flowElement.choices, { id })
                    return choice.value || choice.label
                }),
                rawAnswer: flowAnswer,
            }
        case 'files-select':
            return {
                type: 'files',
                value: flowAnswer.value,
                rawAnswer: flowAnswer,
            }
        case 'extensible-files':
            return {
                type: 'extensible-files',
                value: flowAnswer.value,
                rawAnswer: flowAnswer,
            }
        case 'unit-select':
            return {
                type: 'units',
                value: flowAnswer.value,
                rawAnswer: flowAnswer,
            }
        case 'reports':
            return {
                type: 'reports',
                value: flowAnswer.value,
                rawAnswer: flowAnswer,
            }
        case 'guide':
            return {
                type: 'guide',
                value: Array.isArray(flowAnswer.value) ? flowAnswer.value : [flowAnswer.value],
                rawAnswer: flowAnswer,
            }
        default:
            assumeExhaustion(flowAnswer)
    }
}

function getExhaustiveAnwserFromDatetimeAnswer(flowAnswer: Flows.DateTimeAnswer): Reports.AnswerValue {
    const { value } = flowAnswer
    switch (value.type) {
        case 'date':
            return {
                type: 'date',
                value: formatDate(value.date.day, value.date.month, value.date.year),
                rawAnswer: flowAnswer,
            }
        case 'time':
            return {
                type: 'string',
                value: formatTime(value.time.hour, value.time.minute),
                rawAnswer: flowAnswer,
            }
        case 'datetime':
            return {
                type: 'date',
                value: `${formatDate(value.date.day, value.date.month, value.date.year)}T${formatTime(
                    value.time.hour,
                    value.time.minute
                )}`,
                rawAnswer: flowAnswer,
            }
        default:
            assumeExhaustion(value)
    }
}

function formatTime(hours: number, minutes: number) {
    return `${padToX(hours, 2)}:${padToX(minutes, 2)}`
}

function formatDate(day: number, month: number, year: number) {
    return `${padToX(year, 4)}-${padToX(month, 2)}-${padToX(day, 2)}`
}
